import { createLongAbsence, longAbsenceTypesQuery } from '@hr/api/index.js'
import DefaultDatePicker from '@hr/common/datepicker/DefaultDatePicker'
import { useCurrentUser } from '@hr/hooks/useCurrentUser.ts'
import { translatedCausecode } from '@hr/mgmtview/personalprofile/newabscencemodal/newabscencemodal.constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Alert, Col, Form, Modal, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../../common/Button.tsx'

export function AddLongAbsenceRow({ person }) {
	const { personId } = person
	const currentUser = useCurrentUser()
	const { t } = useTranslation()
	const queryClient = useQueryClient()

	const [causeCode, setCauseCode] = useState('')
	const [startDate, setStartDate] = useState('')
	const [endDate, setEndDate] = useState('')
	const [info, setInfo] = useState('')
	const [showAlert, setShowAlert] = useState(true)

	const [isOverlapModalOpen, setIsOverlapModalOpen] = useState(false)
	const [overlapMessage, setOverlapMessage] = useState('')

	const { data: absenceTypes = [] } = useQuery(longAbsenceTypesQuery)

	const dropdownOptions = useMemo(() => {
		if (!absenceTypes) return []

		return absenceTypes.map((type) => {
			let cleanName = ''

			if (type.causeCode === 'HOURS') {
				cleanName = t('Hours2', 'Tunnit')
			} else if (type.causeCode === 'FLEXI') {
				cleanName = t('FLEXI', 'Saldovapaa')
			} else {
				cleanName = translatedCausecode(type.causeCode, t)
			}

			const paymentStatus = type.paid ? t('Paid', 'palkallinen') : t('Unpaid', 'palkaton')

			return {
				...type,
				translatedName: `${cleanName} (${paymentStatus})`,
			}
		})
	}, [absenceTypes, t])

	const mutation = useMutation({
		mutationFn: createLongAbsence,
		onSuccess: () => {
			setCauseCode('')
			setStartDate('')
			setEndDate('')
			setInfo('')

			queryClient.invalidateQueries({ queryKey: ['absences', personId] })
		},
		onError: (error) => {
			if (error.response?.status === 409 && error.response?.data?.errorKeys?.includes('CALENDAR_EVENT_OVERLAP')) {
				setOverlapMessage(
					t(
						'CalendarEventOverlapMessage',
						'Päivälle on jo merkitty tapahtuma. Poista olemassa oleva tapahtuma ennen uuden lisäämistä.',
					),
				)
				setIsOverlapModalOpen(true)
				return
			}

			const errorMessage = error?.response?.data?.message || error.message
			alert(`${t('ErrorSavingAbsence', 'Virhe tallennettaessa poissaoloa')}: ${errorMessage}`)
		},
	})

	const handleSaveButtonClick = () => {
		if (!causeCode || !startDate || !endDate) {
			alert(t('FillRequiredFields', 'Täytä kaikki pakolliset kentät'))
			return
		}

		const determineApprovers = () => {
			const approvers = []
			const addApprover = (personNumber) =>
				approvers.push({
					id: personNumber,
				})

			// Add current user as the primary approver (if exists)
			if (currentUser?.nr) addApprover(currentUser.nr)

			// Add supervisors as secondary approvers
			const controlDetails = person?.controlDetails
			if (controlDetails) {
				const { supervisor, supervisor2 } = controlDetails
				if (supervisor?.personNumber) addApprover(supervisor.personNumber)
				if (supervisor2?.personNumber) addApprover(supervisor2.personNumber)
			}

			return approvers
		}

		mutation.mutate({
			personId,
			startDate,
			endDate,
			causeCode,
			info: info || '',
			longAbsence: true,
			status: 'APPROVED',
			approvers: determineApprovers(), // [ {id}, {id}, … ] // id property contains the approver person number
		})
	}

	return (
		<div className="w-100">
			{showAlert && (
				<Alert variant="info" onClose={() => setShowAlert(false)} dismissible className="small py-2 px-3 mb-3">
					<p className="mb-1">
						{t('LongAbsenceAlertPart1', 'This action is for adding ')}
						<strong>{t('LongAbsenceAlertBold', 'long unpaid absences only')}</strong>!
					</p>
					<p className="mb-0 text-muted">
						{t(
							'LongAbsenceAlertPart2',
							'Performing this action sets the user to "long-term absence" for the specific period. The absence is also marked in the absence calendar.',
						)}
					</p>
				</Alert>
			)}

			<div className="p-3 border rounded bg-light">
				<Row className="align-items-end">
					<Form.Group as={Col} xl={4} controlId="longAbsenceCauseCode">
						<Form.Label>{t('AbsenceType', 'Poissaolon tyyppi')} *</Form.Label>
						<Form.Select
							value={causeCode}
							onChange={(e) => setCauseCode(e.target.value)}
							style={{
								marginBottom: '6px',
							}}
						>
							<option value="">{t('Choose', 'Valitse')}</option>
							{dropdownOptions.map((type) => (
								<option key={type.causeCode} value={type.causeCode}>
									{type.translatedName}
								</option>
							))}
						</Form.Select>
					</Form.Group>

					<Form.Group as={Col} xl={4} controlId="longAbsenceStartDate">
						<DefaultDatePicker
							id="longAbsence-startDate"
							label={`${t('AbsenceStartDate', 'Alkupäivämäärä')} *`}
							value={startDate}
							onChange={(iso) => setStartDate(iso ?? '')}
							disabled={mutation.isPending}
						/>
					</Form.Group>

					<Form.Group as={Col} xl={4} controlId="longAbsenceEndDate">
						<DefaultDatePicker
							id="longAbsence-endDate"
							label={`${t('EndDate', 'Päättymispäivämäärä')} *`}
							value={endDate}
							onChange={(iso) => setEndDate(iso ?? '')}
							disabled={mutation.isPending}
						/>
					</Form.Group>
				</Row>

				<Row className="align-items-end">
					<Form.Group as={Col} xl={9} controlId="longAbsenceInfo">
						<Form.Label>{t('AbsenceNote', 'Lisätieto')}</Form.Label>
						<Form.Control
							type="text"
							placeholder={t('OptionalNotes', 'Vapaaehtoinen lisätieto')}
							value={info}
							onChange={(e) => setInfo(e.target.value)}
						/>
					</Form.Group>

					<Col xl={3}>
						<button
							type="button"
							className="btn btn-sm w-100 fw-bold"
							disabled={mutation.isPending}
							onClick={handleSaveButtonClick}
							style={{
								backgroundColor: 'transparent',
								color: '#8A2BE2',
								border: '2px solid #8A2BE2',
								transition: 'all 0.2s ease-in-out',
								height: '38px',
								marginBottom: '10px',
							}}
							{...(mutation.isPending && {
								style: {
									backgroundColor: 'transparent',
									color: '#bda3db',
									border: '2px solid #bda3db',
									cursor: 'not-allowed',
									height: '38px',
									marginBottom: '10px',
								},
							})}
						>
							{mutation.isPending ? t('Saving', 'Tallennetaan...') : t('AddLongAbsenceBtn', 'Lisää pitkä poissaolo')}
						</button>
					</Col>
				</Row>
			</div>

			<Modal show={isOverlapModalOpen} size="sm" onHide={() => setIsOverlapModalOpen(false)} centered>
				<Modal.Header closeButton>
					<Modal.Title>{t('CalendarEventOverlapTitle', 'Päällekkäinen tapahtuma')}</Modal.Title>
				</Modal.Header>

				<Modal.Body>{overlapMessage}</Modal.Body>

				<Modal.Footer className="d-flex flex-row justify-content-between">
					<Button variant="cancel" onClick={() => setIsOverlapModalOpen(false)}>
						{t('Close', 'Sulje')}
					</Button>
				</Modal.Footer>
			</Modal>
		</div>
	)
}
